<div mat-dialog-title class="green-manure-header">
  <div>
    <h2>{{ 'greenManure.title' | translate }}</h2>
    <p>{{ displayField().name }} · {{ displayField().areaInHectares | number: '1.2-2' : locale }} ha</p>
  </div>
  <button
    mat-icon-button
    type="button"
    [attr.aria-label]="'close' | translate"
    [disabled]="questionnaire.operation() !== null"
    (click)="close()"
  >
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content class="green-manure-content" data-test="green-manure">
  @if (!contextValid()) {
    <p class="questionnaire-status" role="alert">{{ 'greenManure.contextChanged' | translate }}</p>
  } @else if (questionnaire.loading()) {
    <p class="questionnaire-status" role="status">{{ 'greenManure.loadingQuestionnaire' | translate }}</p>
  }
  <div class="field-context">
    <section class="field-strip" [attr.aria-label]="'cropFieldDetails' | translate">
      <div class="field-strip-header">
        <ng-container *ngTemplateOutlet="fieldInfo" />
        @if (questionnaire.snapshot() !== null) {
          <button
            mat-icon-button
            type="button"
            data-test="refresh-field-snapshot"
            [attr.aria-label]="'greenManure.refreshFieldData' | translate"
            [matTooltip]="'greenManure.refreshFieldData' | translate"
            [disabled]="!canSave() || !snapshotReady()"
            (click)="refreshSnapshot()"
          >
            <mat-icon>refresh</mat-icon>
          </button>
        }
      </div>
      <div class="field-conditions" role="group" [attr.aria-label]="'greenManure.fieldConditions' | translate">
        <span class="field-conditions-label">{{ 'greenManure.fieldConditions' | translate }}</span>
        @for (condition of fieldConditions; track condition.key) {
          <span
            class="field-condition"
            [class.field-condition-registered]="condition.registered === true"
            [class.field-condition-unknown]="condition.registered === null"
            [matTooltip]="'greenManure.' + condition.key + 'Description' | translate"
            tabindex="0"
          >
            <strong>{{ 'greenManure.' + condition.key | translate }}</strong>
            <span>{{
              'greenManure.' +
                (condition.registered === null ? 'unknown' : condition.registered ? 'registered' : 'notRegistered')
                | translate
            }}</span>
          </span>
        }
      </div>
      @if (!snapshotReady()) {
        <div role="status">
          {{ (soilTypesFailed() ? 'greenManure.loadFieldDataFailed' : 'greenManure.loadingFieldData') | translate }}
          @if (soilTypesFailed()) {
            <button mat-button type="button" (click)="retrySoilTypes()">{{ 'greenManure.retry' | translate }}</button>
          }
        </div>
      }
    </section>
  </div>
  @if (contextValid() && !questionnaire.loading()) {
    <div class="guided-flow">
      <app-questionnaire-steps
        [attr.inert]="questionnaire.loadFailed() ? '' : null"
        [class.navigation-disabled]="questionnaire.loadFailed()"
        [attr.aria-disabled]="questionnaire.loadFailed() ? 'true' : null"
        [steps]="steps()"
        [activeStep]="activeStep()"
        (stepSelected)="goToStep($event)"
      />
      <div class="questionnaire-body">
        @if (!questionnaire.loadFailed()) {
          @switch (activeStep()) {
            @case (1) {
              <h3 class="questionnaire-title">{{ 'greenManure.mainCropConditions' | translate }}</h3>
              <h4 id="main-crop-label">{{ 'greenManure.nextMainCrop' | translate }}</h4>
              @switch (cropOptions.state().status) {
                @case ('loading') {
                  <p class="selection-hint" role="status">{{ 'greenManure.loadingCrops' | translate }}</p>
                }
                @case ('failed') {
                  <div role="alert">
                    <p>{{ 'greenManure.loadCropsFailed' | translate }}</p>
                    <button mat-button type="button" (click)="cropOptions.retry()">
                      {{ 'greenManure.retry' | translate }}
                    </button>
                  </div>
                }
                @case ('loaded') {
                  @if (mainCrops().length === 0) {
                    <p class="selection-hint" role="status">{{ 'greenManure.noMainCrops' | translate }}</p>
                  } @else {
                    <mat-form-field appearance="outline" class="main-crop-select">
                      <mat-select
                        aria-labelledby="main-crop-label"
                        [value]="selectedMainCropId()"
                        [placeholder]="'greenManure.mainCropUnknown' | translate"
                        (selectionChange)="selectedMainCropId.set($event.value)"
                      >
                        <mat-option [value]="null">{{ 'greenManure.mainCropUnknown' | translate }}</mat-option>
                        @for (crop of mainCrops(); track crop.id) {
                          <mat-option [value]="crop.id">{{ crop.name }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                }
              }
              <h4 id="tillage-label">{{ 'greenManure.tillageQuestion' | translate }}</h4>
              <mat-radio-group
                class="tillage-options"
                aria-labelledby="tillage-label"
                [value]="isNonInversionTillage()"
                (change)="isNonInversionTillage.set($event.value)"
              >
                <mat-radio-button
                  class="tillage-option"
                  [class.tillage-option-selected]="isNonInversionTillage() === true"
                  color="primary"
                  [value]="true"
                  (click)="isNonInversionTillage.set(true)"
                >
                  {{ 'greenManure.yes' | translate }}
                </mat-radio-button>
                <mat-radio-button
                  class="tillage-option"
                  [class.tillage-option-selected]="isNonInversionTillage() === false"
                  color="primary"
                  [value]="false"
                  (click)="isNonInversionTillage.set(false)"
                >
                  {{ 'greenManure.no' | translate }}
                </mat-radio-button>
              </mat-radio-group>
            }
            @case (2) {
              <h3 class="questionnaire-title">{{ 'greenManure.nematodes' | translate }}</h3>
              <p class="selection-hint" id="nematode-hint">{{ 'greenManure.nematodeHint' | translate }}</p>
              <div
                class="checkbox-options"
                role="group"
                [attr.aria-label]="'greenManure.nematodes' | translate"
                aria-describedby="nematode-hint"
              >
                <mat-checkbox
                  color="primary"
                  class="none-option"
                  [checked]="selectedNematodes()?.length === 0"
                  (change)="setConcern(selectedNematodes, null, $event.checked)"
                >
                  {{ 'greenManure.noNematodes' | translate }}
                </mat-checkbox>
                @for (nematode of nematodes; track nematode.id) {
                  <mat-checkbox
                    color="primary"
                    [checked]="selectedNematodes()?.includes(nematode.id) ?? false"
                    (change)="setConcern(selectedNematodes, nematode.id, $event.checked)"
                  >
                    <ng-container *ngTemplateOutlet="concernLabel; context: { $implicit: nematode }" />
                  </mat-checkbox>
                }
              </div>
            }
            @case (3) {
              <h3 class="questionnaire-title">{{ 'greenManure.diseases' | translate }}</h3>
              <div class="checkbox-options" role="group" [attr.aria-label]="'greenManure.diseases' | translate">
                <mat-checkbox
                  color="primary"
                  class="none-option"
                  [checked]="selectedDiseases()?.length === 0"
                  (change)="setConcern(selectedDiseases, null, $event.checked)"
                >
                  {{ 'greenManure.none' | translate }}
                </mat-checkbox>
                @for (disease of diseases; track disease.id) {
                  <mat-checkbox
                    color="primary"
                    [checked]="selectedDiseases()?.includes(disease.id) ?? false"
                    (change)="setConcern(selectedDiseases, disease.id, $event.checked)"
                  >
                    <ng-container *ngTemplateOutlet="concernLabel; context: { $implicit: disease }" />
                  </mat-checkbox>
                }
              </div>
            }
            @case (4) {
              <h3 id="system-heading" class="questionnaire-title">{{ 'greenManure.systemHeading' | translate }}</h3>
              <mat-radio-group
                class="system-options"
                aria-labelledby="system-heading"
                [value]="selectedSystem()"
                (change)="selectedSystem.set($event.value)"
              >
                @for (system of systems; track system.id) {
                  <mat-radio-button
                    class="system-card"
                    color="primary"
                    [class.system-card-selected]="selectedSystem() === system.id"
                    [value]="system.id"
                    (click)="selectedSystem.set(system.id)"
                  >
                    <span class="system-copy">
                      <mat-icon>{{ system.icon }}</mat-icon>
                      <strong>{{ 'greenManure.' + system.titleKey | translate }}</strong>
                      @if (system.descriptionKey) {
                        <span>{{ 'greenManure.' + system.descriptionKey | translate }}</span>
                      }
                    </span>
                  </mat-radio-button>
                }
              </mat-radio-group>
              <h3 class="questionnaire-title priorities-title">{{ 'greenManure.ecosystemPriorities' | translate }}</h3>
              <p class="selection-hint">{{ 'greenManure.priorityExcluded' | translate }}</p>
              <app-priority-ranking
                [selection]="selectedPriorities()"
                (selectionChange)="selectedPriorities.set($event)"
              />
            }
            @case (5) {
              <h3 class="questionnaire-title">{{ 'greenManure.review' | translate }}</h3>
              <div class="selection-summary">
                <h4>{{ 'greenManure.mainCropConditions' | translate }}</h4>
                <dl class="summary-list">
                  <div>
                    <dt>{{ 'greenManure.nextMainCrop' | translate }}</dt>
                    <dd>
                      @if (selectedMainCrop(); as crop) {
                        <span class="selection-tag">{{ crop.name }}</span>
                      } @else {
                        <span class="selection-hint">{{ 'greenManure.notSelected' | translate }}</span>
                      }
                    </dd>
                  </div>
                  <div>
                    <dt>{{ 'greenManure.tillageQuestion' | translate }}</dt>
                    <dd>
                      @if (isNonInversionTillage() !== null) {
                        <span class="selection-tag">{{
                          'greenManure.' + (isNonInversionTillage() ? 'nonInversion' : 'no') | translate
                        }}</span>
                      } @else {
                        <span class="selection-hint">{{ 'greenManure.notSelected' | translate }}</span>
                      }
                    </dd>
                  </div>
                </dl>
                <h4>{{ 'greenManure.ecosystemPriorities' | translate }}</h4>
                <dl class="summary-list">
                  <div>
                    <dt>{{ 'greenManure.topThree' | translate }}</dt>
                    <dd>
                      @for (priority of rankedPriorities(); track priority.id) {
                        <span class="selection-tag"
                          >{{ priority.rank }}. {{ priorityLabelKeys[priority.id] | translate }}</span
                        >
                      } @empty {
                        <span class="selection-hint">{{ 'greenManure.notSelected' | translate }}</span>
                      }
                    </dd>
                  </div>
                </dl>
                <ng-container
                  *ngTemplateOutlet="
                    concernSummary;
                    context: {
                      titleKey: 'greenManure.nematodes',
                      options: nematodes,
                      selection: selectedNematodes(),
                      noneKey: 'greenManure.noNematodes',
                    }
                  "
                />
                <ng-container
                  *ngTemplateOutlet="
                    concernSummary;
                    context: {
                      titleKey: 'greenManure.diseases',
                      options: diseases,
                      selection: selectedDiseases(),
                      noneKey: 'greenManure.none',
                    }
                  "
                />
                <h4>{{ 'greenManure.system' | translate }}</h4>
                <dl class="summary-list">
                  <div>
                    <dt>{{ 'greenManure.system' | translate }}</dt>
                    <dd>
                      @for (system of systems; track system.id) {
                        @if (selectedSystem() === system.id) {
                          <span class="selection-tag">{{ 'greenManure.' + system.titleKey | translate }}</span>
                        }
                      }
                      @if (!selectedSystem()) {
                        <span class="selection-hint">{{ 'greenManure.notSelected' | translate }}</span>
                      }
                    </dd>
                  </div>
                </dl>
              </div>
            }
          }
        }
        @if (questionnaire.loadFailed()) {
          <div class="questionnaire-status" role="alert">
            <p>{{ 'greenManure.loadQuestionnaireFailed' | translate }}</p>
            <button mat-button type="button" [disabled]="questionnaire.hasUnsavedChanges()" (click)="retryLoad()">
              {{ 'greenManure.retry' | translate }}
            </button>
          </div>
        }
      </div>
    </div>
  }
</mat-dialog-content>

<mat-dialog-actions class="green-manure-actions">
  <div class="persistence-actions">
    <span role="status" aria-live="polite">
      @if (questionnaire.operation() === 'save') {
        <span class="cdk-visually-hidden">{{ 'greenManure.saving' | translate }}</span>
      } @else if (questionnaire.operation() === 'refresh') {
        {{ 'greenManure.refreshing' | translate }}
      } @else {
        @if (questionnaire.hasUnsavedChanges()) {
          <span>{{ 'greenManure.unsavedChanges' | translate }}</span>
        }
      }
    </span>
    @if (questionnaire.error() === 'refresh') {
      <span role="alert">{{ 'greenManure.refreshFailed' | translate }}</span>
    }
  </div>
  @if (contextValid() && !questionnaire.loading()) {
    <div class="step-actions">
      @if (activeStep() > 1) {
        <button mat-button type="button" [disabled]="questionnaire.loadFailed()" (click)="goToStep(activeStep() - 1)">
          {{ 'greenManure.back' | translate }}
        </button>
      }
      @if (activeStep() < steps().length) {
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="!canAdvance() || !canSave()"
          [attr.aria-busy]="questionnaire.operation() === 'save'"
          (click)="goToStep(activeStep() + 1)"
        >
          {{ (activeStep() === steps().length - 1 ? 'greenManure.review' : 'greenManure.next') | translate }}
          @if (questionnaire.operation() === 'save') {
            <mat-spinner diameter="20" strokeWidth="3" [attr.aria-label]="'greenManure.saving' | translate" />
          } @else {
            <mat-icon>arrow_forward</mat-icon>
          }
        </button>
      }
    </div>
  }
</mat-dialog-actions>

<ng-template #fieldInfo>
  <dl class="field-info">
    <div>
      <dt>{{ 'greenManure.cropYear' | translate }}</dt>
      <dd>{{ data.cropYear }}</dd>
    </div>
    <div>
      <dt>{{ 'name' | translate }}</dt>
      <dd>{{ displayField().name }}</dd>
    </div>
    <div>
      <dt>{{ 'hectares' | translate }}</dt>
      <dd>{{ displayField().areaInHectares | number: '1.2-2' : locale }}</dd>
    </div>
    <div>
      <dt>{{ 'crop' | translate }}</dt>
      <dd>{{ displayField().mainCropName || '—' }}</dd>
    </div>
    <div>
      <dt>{{ 'soilType' | translate }}</dt>
      <dd>{{ soilType() || ('greenManure.unknown' | translate) }}</dd>
    </div>
    <div>
      <dt>{{ 'province' | translate }}</dt>
      <dd>{{ displayField().province || '—' }}</dd>
    </div>
    <div>
      <dt>{{ 'startDate' | translate }}</dt>
      <dd>{{ (displayField().startDate | date: 'shortDate' : undefined : locale) || '—' }}</dd>
    </div>
    <div>
      <dt>{{ 'endDate' | translate }}</dt>
      <dd>{{ (displayField().endDate | date: 'shortDate' : undefined : locale) || '—' }}</dd>
    </div>
  </dl>
</ng-template>

<ng-template #concernLabel let-option>
  @if (option.labelKey) {
    {{ option.label }} ({{ option.labelKey | translate }})
  } @else {
    {{ option.label }}
  }
</ng-template>

<ng-template
  #concernSummary
  let-titleKey="titleKey"
  let-options="options"
  let-selection="selection"
  let-noneKey="noneKey"
>
  <h4>{{ titleKey | translate }}</h4>
  <dl class="summary-list">
    <div>
      <dt>{{ titleKey | translate }}</dt>
      <dd>
        @for (entry of options; track entry.id) {
          @if (selection?.includes(entry.id)) {
            <span class="selection-tag"
              ><ng-container *ngTemplateOutlet="concernLabel; context: { $implicit: entry }"
            /></span>
          }
        }
        @if (selection === null) {
          <span class="selection-hint">{{ 'greenManure.notSelected' | translate }}</span>
        }
        @if (selection?.length === 0) {
          <span class="selection-tag">{{ noneKey | translate }}</span>
        }
      </dd>
    </div>
  </dl>
</ng-template>
